/**
 * Pure WCAG contrast helpers. The restaurant brand color is admin-controlled and
 * can be any shade, so text placed on a brand-filled surface must be resolved to a
 * pair that clears AA (>=4.5:1) rather than trusting plain white.
 */

interface Rgb {
  r: number;
  g: number;
  b: number;
}

const WHITE = '#ffffff';
/** slate-900 — the app's dark ink (info-sheet focus ring, headings). */
const DARK = '#0f172a';
const WHITE_RGB: Rgb = { r: 255, g: 255, b: 255 };
const DARK_RGB: Rgb = { r: 15, g: 23, b: 42 };
const AA_NORMAL = 4.5;

function parseHex(hex: string): Rgb | null {
  let h = hex.trim().replace(/^#/, '');
  if (h.length === 3) h = h.replace(/./g, (c) => c + c);
  if (h.length !== 6 || /[^0-9a-fA-F]/.test(h)) return null;
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

function toHex({ r, g, b }: Rgb): string {
  const channel = (n: number) => Math.round(Math.min(255, Math.max(0, n))).toString(16).padStart(2, '0');
  return `#${channel(r)}${channel(g)}${channel(b)}`;
}

/** Linear blend of two colors; `t` is the weight of `b` (0 → all `a`, 1 → all `b`). */
function mix(a: Rgb, b: Rgb, t: number): Rgb {
  return {
    r: a.r + (b.r - a.r) * t,
    g: a.g + (b.g - a.g) * t,
    b: a.b + (b.b - a.b) * t,
  };
}

const BLACK_RGB: Rgb = { r: 0, g: 0, b: 0 };

/** One darkening step, rounded to integer channels so the tested color equals the emitted hex. */
function darkenStep({ r, g, b }: Rgb): Rgb {
  return { r: Math.round(r * 0.9), g: Math.round(g * 0.9), b: Math.round(b * 0.9) };
}

function linearize(channel: number): number {
  const c = channel / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

function relativeLuminance({ r, g, b }: Rgb): number {
  return 0.2126 * linearize(r) + 0.7152 * linearize(g) + 0.0722 * linearize(b);
}

function contrastRatio(a: Rgb, b: Rgb): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la >= lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

export interface AccessibleFill {
  /** Background color to paint on the surface (may be darkened from the brand). */
  fill: string;
  /** Text/icon color guaranteed to clear AA on `fill`. */
  text: string;
}

/**
 * Resolves a brand hex into a `{ fill, text }` pair that meets WCAG AA for normal text.
 * White on the raw brand when it clears AA; else near-black on the raw brand; else the
 * fill is darkened just enough (10% steps) for white to clear — keeping the brand hue.
 * Non-hex input is passed through with white text as a best-effort fallback.
 */
export function resolveAccessibleFill(brandHex: string): AccessibleFill {
  const rgb = parseHex(brandHex);
  if (!rgb) return { fill: brandHex, text: WHITE };

  if (contrastRatio(rgb, WHITE_RGB) >= AA_NORMAL) return { fill: toHex(rgb), text: WHITE };
  if (contrastRatio(rgb, DARK_RGB) >= AA_NORMAL) return { fill: toHex(rgb), text: DARK };

  let darkened = rgb;
  for (let i = 0; i < 12 && contrastRatio(darkened, WHITE_RGB) < AA_NORMAL; i++) {
    darkened = darkenStep(darkened);
  }
  return { fill: toHex(darkened), text: WHITE };
}

/** Darkens the brand hue in 10% steps until it clears AA as text on white. */
function inkOnWhite(rgb: Rgb): string {
  let ink = rgb;
  for (let i = 0; i < 16 && contrastRatio(ink, WHITE_RGB) < AA_NORMAL; i++) {
    ink = darkenStep(ink);
  }
  return toHex(ink);
}

/**
 * Full brand palette derived from a single admin-set primary color. Every token is
 * generated so the restaurant only ever picks ONE color, yet the menu stays coherent
 * and WCAG AA wherever text or icons sit on a brand surface.
 */
export interface BrandPalette {
  /** Raw brand, normalized. Decorative-only fills (dividers, accents) where nothing sits on top. */
  primary: string;
  /** AA background for surfaces that carry text/icons (active tab, buttons). Same resolution as the action bar. */
  fill: string;
  /** Text/icon color guaranteed to clear AA on `fill` (white or near-black). */
  onFill: string;
  /** Brand-tinted text color, darkened just enough to read as text on a white/light background. */
  ink: string;
  /** Very light brand-tinted surface for chips, section headers, hovers on white. */
  tint: string;
  /** One step darker than `fill` for hover/pressed states. */
  hover: string;
  /** Subtle brand-tinted border/ring color on light backgrounds. */
  border: string;
  /** Near-black onyx nudged toward the brand hue; the secondary button's fill. White text always clears AA. */
  brandDark: string;
}

/**
 * Builds a complete, contrast-safe palette from one brand hex. Filled surfaces reuse
 * `resolveAccessibleFill` (so they match the action bar exactly); `ink` is a separate
 * darkening pass because "brand as text on white" needs different handling than "text
 * on a brand fill". Non-hex input degrades to a white-text best effort.
 */
export function generatePalette(brandHex: string): BrandPalette {
  const rgb = parseHex(brandHex);
  const { fill, text } = resolveAccessibleFill(brandHex);
  if (!rgb) {
    return {
      primary: brandHex,
      fill,
      onFill: text,
      ink: brandHex,
      tint: brandHex,
      hover: fill,
      border: brandHex,
      brandDark: DARK,
    };
  }

  const fillRgb = parseHex(fill) ?? rgb;
  return {
    primary: toHex(rgb),
    fill,
    onFill: text,
    ink: inkOnWhite(rgb),
    tint: toHex(mix(WHITE_RGB, rgb, 0.08)),
    hover: toHex(mix(fillRgb, BLACK_RGB, 0.12)),
    border: toHex(mix(WHITE_RGB, rgb, 0.24)),
    brandDark: toHex(mix(DARK_RGB, rgb, 0.18)),
  };
}
